import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './Subject.css';
import { useAuth } from '../../AuthContext';
import { FaEye, FaEdit, FaTrash, FaTimes, FaSort, FaSortUp, FaSortDown, FaUpload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import configData from '../../configure';

const Subjects = ({ orgId }) => {
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    type: '',
    description: '',
    topics: []
  });
  const [currentTopic, setCurrentTopic] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    code: '',
    type: '',
    description: '',
    topics: []
  });
  const [editCurrentTopic, setEditCurrentTopic] = useState('');

  // New states for course suggestions
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showEditSuggestions, setShowEditSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [editFilteredSuggestions, setEditFilteredSuggestions] = useState([]);

  // Sorting states
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });

  const { axios, token, userRole } = useAuth();

  // Refs for handling click outside
  const suggestionRef = useRef(null);
  const editSuggestionRef = useRef(null);

  // Bulk Upload State
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStats, setUploadStats] = useState({ success: 0, failed: 0 });

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setCsvFile(e.target.files[0]);
    }
  };

  const downloadTemplate = () => {
    const headers = ['Course Name,Course Code,Course Type,Description,Topics (semicolon separated)'];
    const rows = [
      'Mathematics,MATH101,Theory,Introduction to Calculus,Algebra;Geometry',
      'Physics Lab,PHY101L,Practical,Basic Physics Experiments,Mechanics;Optics'
    ];
    const csvContent = headers.concat(rows).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'courses_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const ioProcessCSV = (text) => {
    const lines = text.split('\n');
    const result = [];
    // Skip header row
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (line) {
        // Handle comma separation, respecting quotes if present (simple split for now)
        const parts = line.split(',');
        if (parts.length >= 2) {
          const name = parts[0].trim();
          const code = parts[1].trim();
          const type = parts[2] ? parts[2].trim().toLowerCase() : 'theory'; // Default to theory
          // Handle description which might contain commas - simple approach: join remaining parts
          // Ideally use a proper CSV parser library
          let description = "";
          let topics = [];

          if (parts.length > 3) {
            description = parts[3].trim();
          }

          if (parts.length > 4) {
            // Topics might replace commas with semicolons in template or require careful parsing
            // For simplicity, let's assume last column is topics
            // But standard CSV split by comma breaks descriptions with commas. 
            // Let's assume description doesn't have commas for this simple implementation
            // or user puts text in quotes (needs complex parser).
            // As per template: Name, Code, Type, Description, Topics
            const topicString = parts.slice(4).join(','); // Join remaining in case they were split
            if (topicString) {
              topics = topicString.split(';').map(t => t.trim()).filter(t => t);
            }
          }

          if (name && code) {
            result.push({ name, code, type, description, topics });
          }
        }
      }
    }
    return result;
  };

  const handleBulkUpload = async () => {
    if (!csvFile) {
      toast.error("Please select a CSV file first.");
      return;
    }

    setUploading(true);
    setUploadStats({ success: 0, failed: 0 });

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const coursesToCreate = ioProcessCSV(text);

      console.log("Parsed courses:", coursesToCreate);

      if (coursesToCreate.length === 0) {
        toast.warning("No valid courses found in CSV. Please check the format.");
        setUploading(false);
        return;
      }

      // Check for duplicates in the current list (client-side)
      const currentCodes = new Set(subjects.map(c => c.code.toLowerCase()));
      const uniqueCourses = [];
      const duplicateCourses = [];

      coursesToCreate.forEach(course => {
        if (currentCodes.has(course.code.toLowerCase())) {
          duplicateCourses.push(course.code);
        } else {
          // Also check for duplicates within the file itself
          const isInUnique = uniqueCourses.some(u => u.code.toLowerCase() === course.code.toLowerCase());
          if (!isInUnique) {
            uniqueCourses.push(course);
          }
        }
      });

      if (duplicateCourses.length > 0) {
        if (!window.confirm(`Found ${duplicateCourses.length} duplicate course codes (e.g., ${duplicateCourses[0]}). Skip these and continue?`)) {
          setUploading(false);
          return;
        }
      }

      let successCount = 0;
      let failedCount = 0;

      for (const course of uniqueCourses) {
        try {
          const payload = {
            orgId,
            courseCode: course.code,
            courseType: course.type,
            courseDetails: {
              courseName: course.name,
              courseDescription: course.description || "No description provided",
              topics: course.topics
            }
          };

          const response = await fetch(configData.CREATE_SUBJECT, {
            method: 'POST',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': token,
            },
            body: JSON.stringify(payload),
          });

          if (response.ok) {
            successCount++;
          } else {
            console.error(`Failed to create course ${course.code}:`, response.statusText);
            failedCount++;
          }

        } catch (error) {
          console.error(`Error creating course ${course.code}:`, error);
          failedCount++;
        }
        // Update stats progressively
        setUploadStats({ success: successCount, failed: failedCount });
      }

      setUploading(false);
      toast.success(`Bulk upload complete! Success: ${successCount}, Failed: ${failedCount}`);
      setIsUploadModalOpen(false);
      setCsvFile(null);
      fetchSubjects(); // Refresh list
    };

    reader.onerror = () => {
      toast.error("Error reading file");
      setUploading(false);
    };

    reader.readAsText(csvFile);
  };

  // Predefined course names
  const predefinedCourseNames = [
    "Mathematics",
    "Physics",
    "Chemistry",
    "Biology",
    "Computer Science",
    "English Literature",
    "History",
    "Economics",
    "Psychology",
    "Art & Design",
    "Physical Education",
    "Music",
    "Geography",
    "Philosophy",
    "Sociology",
    "Political Science",
    "Statistics",
    "Business Studies",
    "Accounting",
    "Engineering Drawing",
    "Environmental Science",
    "Foreign Language",
    "Data Science",
    "Information Technology",
    "Mechanical Engineering",
    "Electrical Engineering",
    "Civil Engineering",
    "Architecture",
    "Medicine",
    "Law"
  ];

  // Course type options
  const courseTypeOptions = [
    { value: '', label: 'Select Course Type' },
    { value: 'theory', label: 'Theory / Lecture-Based' },
    { value: 'practical', label: 'Practical / Lab-Based' },
    { value: 'tutorial', label: 'Tutorial' },
    { value: 'seminar', label: 'Seminar' },
    { value: 'workshop', label: 'Workshop' }
  ];

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon for column headers
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <FaSort className="sort-icon" />;
    }
    return sortConfig.direction === 'asc' ?
      <FaSortUp className="sort-icon active" /> :
      <FaSortDown className="sort-icon active" />;
  };

  // Sort subjects based on current sort configuration
  const getSortedSubjects = (subjectsToSort) => {
    if (!sortConfig.key) {
      return subjectsToSort;
    }

    return [...subjectsToSort].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Handle clicks outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
      if (editSuggestionRef.current && !editSuggestionRef.current.contains(event.target)) {
        setShowEditSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter suggestions based on input
  const filterSuggestions = (inputValue) => {
    if (!inputValue.trim()) {
      return predefinedCourseNames;
    }

    return predefinedCourseNames.filter(courseName =>
      courseName.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  // Handle course name input change with suggestions
  const handleCourseNameChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, name: value }));

    const filtered = filterSuggestions(value);
    setFilteredSuggestions(filtered);
    setShowSuggestions(true);
  };

  // Handle edit course name input change with suggestions
  const handleEditCourseNameChange = (e) => {
    const value = e.target.value;
    setEditFormData(prev => ({ ...prev, name: value }));

    const filtered = filterSuggestions(value);
    setEditFilteredSuggestions(filtered);
    setShowEditSuggestions(true);
  };

  // Handle course name input focus
  const handleCourseNameFocus = () => {
    const filtered = filterSuggestions(formData.name);
    setFilteredSuggestions(filtered);
    setShowSuggestions(true);
  };

  // Handle edit course name input focus
  const handleEditCourseNameFocus = () => {
    const filtered = filterSuggestions(editFormData.name);
    setEditFilteredSuggestions(filtered);
    setShowEditSuggestions(true);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (courseName) => {
    setFormData(prev => ({
      ...prev,
      name: courseName
    }));
    setShowSuggestions(false);
  };

  // Handle edit suggestion selection
  const handleEditSuggestionSelect = (courseName) => {
    setEditFormData(prev => ({
      ...prev,
      name: courseName
    }));
    setShowEditSuggestions(false);
  };

  // Fetch subjects from the API
  const fetchSubjects = async () => {
    if (!token) {
      console.error('No token available for fetching subjects');
      return;
    }

    try {
      const response = await fetch(
        `${configData.GET_SUBJECTS_BY_ORG}/${orgId}`,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Fetched subjects:', data);

      if (!Array.isArray(data)) {
        console.error('Expected an array but got:', data);
        setSubjects([]);
        return;
      }

      const mappedSubjects = data
        .filter(course => course.courseDetails && course.courseDetails.courseName)
        .map(course => ({
          id: course.courseId || '',
          name: course.courseDetails.courseName || 'Unknown',
          code: course.courseCode || 'N/A',
          type: course.courseType
            ? (course.courseType.charAt(0).toUpperCase() + course.courseType.slice(1))
            : 'Unknown',
          description: course.courseDetails.courseDescription || 'No description available',
          topics: course.courseDetails.topics || []
        }));
      setSubjects(mappedSubjects);
    } catch (error) {
      console.error('Error fetching subjects:', error);
      setSubjects([]);
    }
  };

  useEffect(() => {
    fetchSubjects();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      handleCourseNameChange(e);
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleAddTopic = (e) => {
    e.preventDefault();
    if (currentTopic.trim() && !formData.topics.includes(currentTopic.trim())) {
      setFormData(prev => ({
        ...prev,
        topics: [...prev.topics, currentTopic.trim()]
      }));
      setCurrentTopic('');
    }
  };

  const handleRemoveTopic = (topicToRemove) => {
    setFormData(prev => ({
      ...prev,
      topics: prev.topics.filter(topic => topic !== topicToRemove)
    }));
  };

  const handleTopicKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddTopic(e);
    }
  };

  // Edit modal functions
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'name') {
      handleEditCourseNameChange(e);
    } else {
      setEditFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleEditAddTopic = (e) => {
    e.preventDefault();
    if (editCurrentTopic.trim() && !editFormData.topics.includes(editCurrentTopic.trim())) {
      setEditFormData(prev => ({
        ...prev,
        topics: [...prev.topics, editCurrentTopic.trim()]
      }));
      setEditCurrentTopic('');
    }
  };

  const handleEditRemoveTopic = (topicToRemove) => {
    setEditFormData(prev => ({
      ...prev,
      topics: prev.topics.filter(topic => topic !== topicToRemove)
    }));
  };

  const handleEditTopicKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleEditAddTopic(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.code.trim()) return;

    if (!token) {
      console.error('No token available for creating subject');
      return;
    }

    const payload = {
      orgId,
      courseCode: formData.code,
      courseType: formData.type.toLowerCase(),
      courseDetails: {
        courseName: formData.name,
        courseDescription: formData.description || "No description provided",
        topics: formData.topics,
      }
    };

    try {
      const response = await fetch(configData.CREATE_SUBJECT, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': token,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(`Subject "${formData.name}" created successfully!`);
        await fetchSubjects();
      } else {
        toast.error(`Failed to create subject: ${response.statusText}`);
        console.error('Failed to create subject:', response.statusText);
      }
    } catch (error) {
      toast.error('An error occurred while creating the subject.');
      console.error('Error creating subject:', error);
    }

    setFormData({ name: '', code: '', type: '', description: '', topics: [] });
    setCurrentTopic('');
  };

  const handleEdit = (subject) => {
    setEditFormData({
      name: subject.name,
      code: subject.code,
      type: subject.type.toLowerCase(), // Convert to lowercase for dropdown value
      description: subject.description || '',
      topics: subject.topics || []
    });
    setEditingId(subject.id);
    setShowEditModal(true);
  };

  const handleUpdateSubject = async (e) => {
    e.preventDefault();
    if (!editFormData.name.trim() || !editFormData.code.trim()) return;

    if (!token) {
      console.error('No token available for updating subject');
      return;
    }

    // Prepare the payload for the PUT request
    const payload = {
      courseId: editingId,
      orgId,
      courseCode: editFormData.code,
      courseType: editFormData.type.toLowerCase(),
      courseDetails: {
        courseName: editFormData.name,
        courseDescription: editFormData.description || "No description provided",
        topics: editFormData.topics
      }
    };

    try {
      const response = await fetch(`${configData.UPDATE_SUBJECT}/${editingId}`, {
        method: 'PUT',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': token,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(`Subject "${editFormData.name}" updated successfully!`);
        await fetchSubjects();
      } else {
        toast.error(`Failed to update subject: ${response.statusText}`);
        console.error('Failed to update subject:', response.statusText);
        return;
      }
    } catch (error) {
      toast.error('An error occurred while updating the subject.');
      console.error('Error updating subject:', error);
      return;
    }

    // Close modal and reset only if the API call was successful
    setShowEditModal(false);
    setEditingId(null);
    setEditFormData({ name: '', code: '', type: '', description: '', topics: [] });
    setEditCurrentTopic('');
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingId(null);
    setEditFormData({ name: '', code: '', type: '', description: '', topics: [] });
    setEditCurrentTopic('');
  };

  const handleDelete = async (id) => {
    if (!token) {
      console.error('No token available for deleting subject');
      return;
    }

    try {
      const response = await fetch(`${configData.DELETE_SUBJECT}/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/hal+json',
          'Authorization': token,
        },
      });

      if (response.ok) {
        toast.success('Subject deleted successfully!');
        await fetchSubjects();
      } else {
        toast.error(`Failed to delete subject: ${response.statusText}`);
        console.error('Failed to delete subject:', response.statusText);
      }
    } catch (error) {
      toast.error('An error occurred while deleting the subject.');
      console.error('Error deleting subject:', error);
    }

    if (editingId === id) {
      setEditingId(null);
      setEditFormData({ name: '', code: '', type: '', description: '', topics: [] });
      setShowEditModal(false);
    }
  };

  // Updated handleView function to navigate to SubjectView component
  const handleView = (subject) => {
    navigate(`/courses/${subject.code}`);
  };

  // Apply filtering and sorting
  const filteredSubjects = subjects.filter(subject =>
    subject.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subject.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedAndFilteredSubjects = getSortedSubjects(filteredSubjects);

  return (
    <div className="subject-container">
      <div className="subject-form">
        <div className="form-layout">
          <div className="input-container" ref={suggestionRef}>
            <label>Course Name</label>
            <input
              type="text"
              name="name"
              placeholder="e.g., Biology"
              value={formData.name}
              onChange={handleInputChange}
              onFocus={handleCourseNameFocus}
              className="form-input"
              autoComplete="off"
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div className="suggestions-dropdown">
                {filteredSuggestions.slice(0, 8).map((courseName, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleSuggestionSelect(courseName)}
                  >
                    {courseName}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="input-container">
            <label>Course Code</label>
            <input
              type="text"
              name="code"
              placeholder="e.g., Bio101"
              value={formData.code}
              onChange={handleInputChange}
              className="form-input"
            />
          </div>
          <div className="input-container">
            <label>Course Type</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="form-input"
            >
              {courseTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="input-container">
            <label>Description</label>
            <input
              name="description"
              placeholder="Enter Course description..."
              value={formData.description}
              onChange={handleInputChange}
              className="form-input"
              rows="3"
            />
          </div>
          <div className="input-container">
            <label>Topics</label>
            <div className="topics-input-container">
              <input
                type="text"
                placeholder="Enter a topic and press Enter or click Add"
                value={currentTopic}
                onChange={(e) => setCurrentTopic(e.target.value)}
                onKeyPress={handleTopicKeyPress}
                className="form-input"
              />
              <button
                type="button"
                onClick={handleAddTopic}
                className="add-topic-btn"
                disabled={!currentTopic.trim()}
              >
                Add
              </button>
            </div>
            {formData.topics.length > 0 && (
              <div className="topics-list">
                {formData.topics.map((topic, index) => (
                  <span key={index} className="topic-tag">
                    {topic}
                    <button
                      type="button"
                      onClick={() => handleRemoveTopic(topic)}
                      className="remove-topic-btn"
                      title="Remove topic"
                    >
                      <FaTimes />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          {userRole !== 'STUDENT' && (
            <>
              <button type="submit" onClick={handleSubmit} className="create-btn">
                Create
              </button>
              <button
                type="button"
                className="bulk-upload-btn"
                onClick={() => setIsUploadModalOpen(true)}
              >
                <FaUpload /> Bulk Upload
              </button>
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Course</h3>
              <button className="close-btn" onClick={handleCloseModal}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleUpdateSubject}>
                <div className="modal-form-layout">
                  <div className="input-container" ref={editSuggestionRef}>
                    <label>Course Name</label>
                    <input
                      type="text"
                      name="name"
                      placeholder="e.g., Biology"
                      value={editFormData.name}
                      onChange={handleEditInputChange}
                      onFocus={handleEditCourseNameFocus}
                      className="form-input"
                      autoComplete="off"
                    />
                    {showEditSuggestions && editFilteredSuggestions.length > 0 && (
                      <div className="suggestions-dropdown">
                        {editFilteredSuggestions.slice(0, 8).map((courseName, index) => (
                          <div
                            key={index}
                            className="suggestion-item"
                            onClick={() => handleEditSuggestionSelect(courseName)}
                          >
                            {courseName}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="input-container">
                    <label>Course Code</label>
                    <input
                      type="text"
                      name="code"
                      placeholder="e.g., Bio101"
                      value={editFormData.code}
                      onChange={handleEditInputChange}
                      className="form-input"
                    />
                  </div>
                  <div className="input-container">
                    <label>Course Type</label>
                    <select
                      name="type"
                      value={editFormData.type}
                      onChange={handleEditInputChange}
                      className="form-input"
                    >
                      {courseTypeOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="input-container">
                    <label>Description</label>
                    <textarea
                      name="description"
                      placeholder="Enter Course description..."
                      value={editFormData.description}
                      onChange={handleEditInputChange}
                      className="form-input"
                      rows="3"
                    />
                  </div>
                  <div className="input-container">
                    <label>Topics</label>
                    <div className="topics-input-container">
                      <input
                        type="text"
                        placeholder="Enter a topic and press Enter or click Add"
                        value={editCurrentTopic}
                        onChange={(e) => setEditCurrentTopic(e.target.value)}
                        onKeyPress={handleEditTopicKeyPress}
                        className="form-input"
                      />
                      <button
                        type="button"
                        onClick={handleEditAddTopic}
                        className="add-topic-btn"
                        disabled={!editCurrentTopic.trim()}
                      >
                        Add
                      </button>
                    </div>
                    {editFormData.topics.length > 0 && (
                      <div className="topics-list">
                        {editFormData.topics.map((topic, index) => (
                          <span key={index} className="topic-tag">
                            {topic}
                            <button
                              type="button"
                              onClick={() => handleEditRemoveTopic(topic)}
                              className="remove-topic-btn"
                              title="Remove topic"
                            >
                              <FaTimes />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" onClick={handleCloseModal} className="cancel-btn">
                    Cancel
                  </button>
                  <button type="submit" className="update-btn">
                    Update Course
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="subject-list">
        <div className="list-header">
          <h3>Course List</h3>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by Subject"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <span className="search-icon">🔍</span>
          </div>
        </div>

        <div className="table-container">
          <table className="subject-table">
            <thead>
              <tr>
                <th
                  className="sortable-header"
                  onClick={() => handleSort('name')}
                  title="Click to sort by Course Name"
                >
                  Course Name {getSortIcon('name')}
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort('code')}
                  title="Click to sort by Course Code"
                >
                  Course Code {getSortIcon('code')}
                </th>
                <th
                  className="sortable-header"
                  onClick={() => handleSort('type')}
                  title="Click to sort by Course Type"
                >
                  Course Type {getSortIcon('type')}
                </th>
                <th>Description</th>
                <th>Topics</th>
                {userRole !== 'STUDENT' && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {sortedAndFilteredSubjects.map((subject) => (
                <tr key={subject.id}>
                  <td>{subject.name}</td>
                  <td>{subject.code}</td>
                  <td>{subject.type}</td>
                  <td className="description-cell">
                    {subject.description ?
                      (subject.description.length > 50 ?
                        `${subject.description.substring(0, 50)}...` :
                        subject.description
                      ) :
                      'No description'
                    }
                  </td>
                  <td className="topics-cell">
                    {subject.topics && subject.topics.length > 0 ? (
                      <div className="topics-preview">
                        {subject.topics.slice(0, 2).map((topic, index) => (
                          <span key={index} className="topic-preview-tag">
                            {topic}
                          </span>
                        ))}
                        {subject.topics.length > 2 && (
                          <span className="more-topics">
                            +{subject.topics.length - 2} more
                          </span>
                        )}
                      </div>
                    ) : (
                      'No topics'
                    )}
                  </td>
                  {userRole !== 'STUDENT' && (
                    <td>
                      <div className="action-buttons">
                        <button
                          onClick={() => handleView(subject)}
                          className="action-btn vi-btn"
                          title="View"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => handleEdit(subject)}
                          className="action-btn edit-btn"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(subject.id)}
                          className="action-btn delete-btn"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>


      {/* Upload Modal */}
      {
        isUploadModalOpen && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Bulk Upload Courses</h3>
                <button className="modal-close-btn" onClick={() => setIsUploadModalOpen(false)}>&times;</button>
              </div>
              <div className="modal-body">
                <p style={{ color: '#64748b', marginBottom: '20px' }}>
                  Upload a CSV file to create multiple courses at once.
                  The file must include <strong>Course Name</strong> and <strong>Course Code</strong> columns.
                </p>

                <div className="form-group">
                  <div className="file-upload-wrapper">
                    <input type="file" accept=".csv" onChange={handleFileChange} />
                    <div className="file-upload-content">
                      <div className="upload-icon">
                        <FaUpload />
                      </div>
                      <div className="upload-text">
                        {csvFile ? csvFile.name : "Click or drag CSV file here"}
                      </div>
                      {!csvFile && <div className="upload-hint">Supported format: .csv</div>}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <button
                    onClick={downloadTemplate}
                    className="save-btn"
                    style={{
                      backgroundColor: 'white',
                      color: '#2563eb',
                      border: '1px solid #2563eb',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    Download Sample Template
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button className="cancel-btn" onClick={() => setIsUploadModalOpen(false)}>Cancel</button>
                <button
                  className="save-btn"
                  onClick={handleBulkUpload}
                  disabled={uploading || !csvFile}
                >
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )
      }

    </div >
  );
};

export default Subjects;